import { createTheme } from '@mui/material/styles'
import { ThemeOptions, Theme } from '@mui/material/styles/createTheme'
import { PaletteOptions } from '@mui/material/styles/createPalette'
import { PaletteMode } from '@mui/material'

const round = (value: number) => {
  return Math.round(value * 1e5) / 1e5
}

export const rem = (value: number): string => {
  return `${round(value / 16)}rem`
}

const sohneFontFamily = ['Söhne'].join(',')

declare module '@mui/material/styles/createTheme' {
  interface Theme {
    customPalette: {
      light: string
      lightGray: string
      lightGray2: string
      mediumGray: string
      mediumGray2: string
      darkGray: string
      darkGray2: string
      dark: string
      darkGreen: string
      lightBlue: string
      darkBlue: string
      warning: string
      error: string
      border: string
      pageBackground: string
      background: string
      bodyBackground: string
      footer: string
      navActiveBg: string
      navText: string
    }
  }

  interface ThemeOptions {
    customPalette?: {
      light: string
      lightGray: string
      lightGray2: string
      mediumGray: string
      mediumGray2: string
      darkGray: string
      darkGray2: string
      dark: string
      darkGreen: string
      lightBlue: string
      darkBlue: string
      warning: string
      error: string
      border: string
      pageBackground: string
      background: string
      bodyBackground: string
      footer: string
      navActiveBg: string
      navText: string
      iconGray: string
    }
  }
}

export function createMyTheme(type: PaletteMode, options: ThemeOptions): Theme {
  return createTheme({
    customPalette: {
      light: '#FFFFFF',
      lightGray: '#F2F2F2',
      lightGray2: '#D8D8D8',
      mediumGray: '#C4C4C4',
      mediumGray2: '#A5A5A5',
      darkGray: '#686868',
      darkGray2: '#555555',
      dark: '#4A4A4A',
      darkGreen: '#032615',
      lightBlue: '#4990E2',
      darkBlue: '#003478',
      warning: '#F5A623',
      error: '#E03333',
      border: type === 'light' ? '#F2F2F2' : '#777777',
      background: type === 'light' ? '#F2F2F2' : '#686868',
      pageBackground: type === 'light' ? '#CFCFCF' : '#262626',
      bodyBackground: type === 'light' ? '#FFFFFF' : '#444444',
      footer: '#9b9b9b',
      navActiveBg: '#F7F9FB',
      navText: '#032615',
      iconGray: '#373d40',
    },
    ...options,
  })
}

const palette = (type: PaletteMode): PaletteOptions => {
  return {
    mode: type,
    primary: {
      main: '#1AA85D',
      dark: '#0B8E50',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#4990E2',
    },
  }
}

const materialUITheme = (type: PaletteMode): Theme => {
  return createMyTheme(type, {
    palette: palette(type),
    typography: {
      fontFamily: sohneFontFamily,
      body1: {
        fontSize: rem(15),
      },
      body2: {
        fontSize: rem(15),
        color: type === 'light' ? '#555555' : '#FFFFFF',
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            fontFamily: sohneFontFamily,
          },
          body: {
            fontFamily: sohneFontFamily,
          },
          '#root': {
            fontFamily: sohneFontFamily,
          },
          'button, input, select, textarea': {
            fontFamily: 'inherit',
          },
          'b, strong': {
            fontWeight: 500,
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          h1: {
            fontSize: rem(22),
            lineHeight: 1.375,
            fontWeight: 700,
            fontStyle: 'italic',
          },
          h2: {
            fontSize: rem(22),
            lineHeight: 1.375,
            fontWeight: 700,
            fontStyle: 'italic',
            color: '#1AA85D',
            display: 'flex',
            alignItems: 'center',
          },
          h3: {
            fontSize: rem(20),
            lineHeight: 1.375,
            fontWeight: 700,
            fontStyle: 'italic',
            color: '#1AA85D',
            display: 'flex',
            alignItems: 'center',
          },
          h4: {
            fontSize: rem(18),
            lineHeight: 1.375,
            fontWeight: 700,
            color: type === 'light' ? '#555555' : '#FFFFFF',
            display: 'flex',
            alignItems: 'center',
          },
          h5: {
            fontSize: rem(20),
            fontWeight: 500,
            lineHeight: 1.2,
          },
          h6: {
            fontSize: rem(16),
            fontWeight: 500,
            lineHeight: 1.2,
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            margin: '24px 0',
            backgroundColor: '#1AA85D',
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            backgroundColor: 'transparent',
          },
        },
      },
      MuiTable: {
        styleOverrides: {
          root: {
            '&.leaderboard': {
              tableLayout: 'auto',
              '& > thead > tr > th': {
                color: 'inherit',
                fontWeight: 'bold',
                textAlign: 'center',
                fontSize: rem(15),
                paddingBottom: 10,
                borderBottom: 0,
                '&:first-of-type': {
                  color: '#777777',
                },
                '&:nth-of-type(6), &:nth-last-of-type(2)': {
                  color: '#1AA85D',
                },
                '&:nth-of-type(4)': {
                  textAlign: 'left',
                },
              },
              '& > tbody > tr > td': {
                borderBottom: 0,
                textAlign: 'center',
                fontSize: rem(14),
                '&:nth-of-type(6), &:nth-last-of-type(2)': {
                  color: '#1AA85D',
                  fontWeight: 'bold',
                },
                '&:nth-of-type(4)': {
                  textAlign: 'left',
                },
                '&:nth-of-type(2)': {
                  color: '#BEBEBE',
                },
              },
              '@global': {
                '.MuiTableBody-root > .MuiTableRow-root.withBg': {
                  background: 'rgba(26,168,93,0.08)',
                },
              },
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontSize: rem(15),
            color: '#A4A4A4',
            '@global': {
              svg: {
                width: '0.7em',
              },
            },
          },
          body: {
            fontSize: rem(14),
          },
        },
      },
      MuiTableSortLabel: {
        styleOverrides: {
          root: {
            color: '#A4A4A4',
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            fontSize: rem(12),
            color: '#1AA85D',
            letterSpacing: 0.4,
            lineHeight: 1.1,
            marginBottom: 5,
            '&.SwitchLabel': {
              fontSize: rem(14),
              color: type === 'light' ? '#555555' : '#FFFFFF',
              marginBottom: 0,
              display: 'flex',
              alignItems: 'center',
            },
          },
        },
      },
      MuiFormControl: {
        styleOverrides: {
          marginNormal: {
            marginTop: 6,
          },
          root: {
            '&.SwitchControl': {
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            left: -12,
          },
        },
      },
      MuiStepConnector: {
        styleOverrides: {
          alternativeLabel: {
            top: 10,
            left: '-50%',
            right: '50%',
            zIndex: 0,
          },
          lineHorizontal: {
            borderTopWidth: 4,
            borderColor: '#1aa85d',
          },
        },
      },
      MuiStepButton: {
        styleOverrides: {
          root: {
            zIndex: 1,
          },
        },
      },
      MuiStepLabel: {
        styleOverrides: {
          root: {
            zIndex: 1,
          },
          alternativeLabel: {
            fontSize: rem(16),
            color: '#1aa85d !important',
            fontWeight: 300,
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            backgroundColor: type === 'light' ? '#F2F2F2' : '#686868',
            marginBottom: 16,
            '@global': {
              h6: {
                fontSize: rem(24),
                lineHeight: 1.4,
              },
            },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            fontSize: rem(15),
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          adornedEnd: {
            paddingRight: 4,
          },
          input: {
            paddingTop: 16,
            paddingBottom: 16,
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            minWidth: undefined,
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '16px 24px 24px',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: undefined,
            fontSize: rem(15),
            fontWeight: 400,
            minWidth: 110,
          },
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
            },
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          underlineHover: {
            textDecoration: 'underline',
            '&:hover': {
              textDecoration: 'none',
            },
          },
        },
      },
      MuiSvgIcon: {
        styleOverrides: {
          colorDisabled: {
            color: '#C4C4C4',
          },
        },
      },
      MuiFab: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            color: '#FFFFFF',
            backgroundColor: '#555555',
            minHeight: 30,
          },
          sizeSmall: {
            width: 30,
            height: 30,
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          icon: {
            color: 'inherit',
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: '#FFFFFF',
            color: '#404040',
            boxShadow:
              // eslint-disable-next-line max-len
              '0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12)',
            borderRadius: '4px',
          },
        },
      },
    },
  })
}

export default materialUITheme
